import React, {
  memo,
  type ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  Badge,
  Box,
  ButtonIcon,
  ButtonSelect,
  Icon,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodAdvancedFilters, SodSort } from '../../../../shared/components';
import SodSearch from '../../../../shared/components/SodControls/SodSearch';
import {
  ABORT_ERROR,
  CF_NEU_MED,
  CF_NEU_STL,
  DEBOUNCE_TIME_FILTER_SEARCH,
  DEFAULT_AD_FILTER_PARAMS,
  testIds,
} from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { type AdvancedFilterState } from '../../../../shared/types';
import { type EntitlementColumnState } from '../../data-layer';
import { entitlementActions } from '../../data-store';
import { useFilterCache, useGetQueryMap, useGetSortMenu } from '../../hooks';
import EntitlementColumnList from '../EntitlementColumnList';

import './EntitlementControls.css';

/**
 * EntitlementControls Component
 *
 * Comprehensive control panel for managing entitlement list interactions including search,
 * filtering, sorting, and column management with real-time updates, caching capabilities,
 * and responsive design for optimal user experience.
 *
 * @component
 * @returns {React.ReactElement} Control panel with search, filters, and management tools including:
 *   - Real-time search with debouncing
 *   - Advanced filtering with caching
 *   - Multi-field sorting options
 *   - Column visibility management
 *   - Total count display
 *
 * @example
 * <EntitlementControls />
 *
 * @dependencies
 * - Redux: filters, activeFilters, activeColumns, totalCount from state.aag.entitlement
 * - Hooks: useFilterCache, useGetQueryMap, useGetSortMenu
 * - Components: SodAdvancedFilters, SodSort, SodSearch, EntitlementColumnList
 */
function EntitlementControls(): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();

  const {
    aag: {
      entitlement: {
        controls: {
          filters,
          filterCache,
          activeFilters,
          activeColumns,
          sortInAction,
          isInsightsOpen,
          activeInsight,
        },
        sidepanel: { isSidePanelVisible },
        list: { totalCount, isListLoading },
        // bulkaction: { isBulkChecked },
      },
    },
    user: { isMenuPinned },
  } = useTypedSelector((state) => state);

  // Hook for sort functionality
  const {
    fields,
    fieldName,
    actions,
    actionName,
    handleFieldClick,
    handleActionClick,
  } = useGetSortMenu(sortInAction);
  //  Hooks for filter
  const queryMap = useGetQueryMap();
  const { getFromCache, setCache } = useFilterCache(
    filterCache,
    entitlementActions.setFiltersCache
  );

  const isClearBtn = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  // Filter
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      const cacheResponse = getFromCache(key, searchText);

      if (cacheResponse) {
        return cacheResponse as MenuOption[];
      }

      // Abort should only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        const fn = (
          queryMap as Record<
            string,
            (params: {
              search: string;
              key: string;
              [key: string]: unknown;
            }) => {
              abort: () => void;
              unwrap: () => Promise<MenuOption[]>;
            }
          >
        )[key];

        const controller = fn({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
          key,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response.map((item) => item);

        setCache(key, searchText, options);

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        Logger.log('error', err.message);

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [queryMap, getFromCache, setCache]
  );

  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        dispatch(entitlementActions.setActiveFilters(newActiveFilters));

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) => {
            if (filter.value === key) {
              return {
                ...filter,
                options: selectedOptions,
              };
            }

            return filter;
          })
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      dispatch(entitlementActions.setActiveFilters(newActiveFilters));
    },
    [activeFilters, dispatch]
  );

  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      const isFilterApplied = activeFilters.find(
        (filter) => filter.value === selectedFilter.value
      );

      if (selectedFilter.isChecked && isFilterApplied) {
        handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
      }

      const modifiedFilters = filters.map((filter) => {
        if (selectedFilter.value === filter.value) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      });

      dispatch(entitlementActions.setAdvancedFilters(modifiedFilters));
    },
    [dispatch, filters, activeFilters, handleOptionsClick]
  );

  const handleClearBtn = useCallback(() => {
    dispatch(entitlementActions.setActiveFilters([]));
  }, [dispatch]);

  const handleSearch = useCallback(
    (searchText: string) => {
      dispatch(entitlementActions.setSearchKey(searchText));
    },
    [dispatch]
  );

  const handleColumnsChange = useCallback(
    (columns: EntitlementColumnState[]) => {
      dispatch(entitlementActions.setViewColumns(columns));
    },
    [dispatch]
  );

  const handleInsights = useCallback(() => {
    if (isSidePanelVisible) {
      dispatch(entitlementActions.setIsSidePanelVisible(false));
      dispatch(entitlementActions.setIsInsightsOpen(true));
    } else {
      dispatch(entitlementActions.setIsInsightsOpen(!isInsightsOpen));
    }
  }, [dispatch, isInsightsOpen, isSidePanelVisible]);

  const isInsightsDisabled = useMemo(
    () => Boolean(isListLoading),
    [isListLoading]
  );

  // For selected insights
  const renderActiveKind = useCallback(() => {
    if (activeInsight && isInsightsOpen) {
      return (
        <Box className='Sod-ViolationsEntitlement-controls-activeInsight'>
          <Typography
            kind='body2'
            color={CF_NEU_MED}
            className='Sod-EntitlementControls-activeKindWrapper--isInsight'>
            {l('Displaying items related to the insight: ')}
            <span className='Sod-EntitlementControls-activeKindLinkText'>
              {activeInsight.title}
            </span>
          </Typography>
        </Box>
      );
    }

    return null;
  }, [l, activeInsight, isInsightsOpen]);

  const wrapperClasses = cx(
    'Sod-EntitlementControls',
    isMenuPinned && 'Sod-EntitlementControls--isMenuPinned'
  );

  const advancedFilterClasses = cx(
    'Sod-Entitlement-SodAdvancedFilters',
    isFilterBarOpen
      ? 'SodAdvancedFilters--isOpen'
      : 'SodAdvancedFilters--isClose'
  );

  return (
    <Box className={wrapperClasses}>
      <Box className='Sod-ViolationsEntitlement-controls-wrapper'>
        <Box className='Sod-ViolationsEntitlement-controls'>
          <Box className='Sod-ViolationsEntitlement-controlsLeft'>
            <SodSearch
              searchPlaceholder={l('Search')}
              inlineMesssage={l('Type more than 2 characters to see results.')}
              searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
              isDisabled={false}
              handleSearch={handleSearch}
              isSearchAlwaysExpanded
            />
            <ButtonSelect
              kind='rectangle'
              label={l('Filter')}
              onClick={() => setIsFilterBarOpen(!isFilterBarOpen)}
              isOpen={false}
              size='medium'
              isDisabled={isListLoading}
              prefixIcon={<Icon kind='Filter' />}
              isVisualCueEnabled={false}
              BadgeComp={
                activeFilters.length ? (
                  <Badge kind='dot' color='primary' size='small' />
                ) : null
              }
            />
          </Box>
        </Box>
        {/* Filters */}
        <SodAdvancedFilters
          filters={filters}
          activeFilters={activeFilters}
          isClearBtn={isClearBtn}
          isDisabled={isListLoading}
          searchDebounce={DEBOUNCE_TIME_FILTER_SEARCH}
          handleFilterChange={handleFilterChange}
          handleFilterSearch={handleFilterSearch}
          handleOptionsClick={handleOptionsClick}
          handleClearBtn={handleClearBtn}
          className={advancedFilterClasses}
          isAddOrRemoveBtn={false}
        />
      </Box>
      {renderActiveKind()}
      <Box className='Sod-EntitlementControls-groupControls'>
        <Box className='Sod-ViolationsEntitlement-listSubheadingWrapper'>
          {!isListLoading && (
            <Typography kind='body3' color={CF_NEU_STL}>
              {l(
                '{count, plural, one {# Entitlement} other {# Entitlements}}',
                {
                  count: totalCount ?? 0,
                }
              )}
            </Typography>
          )}
        </Box>
        <Box className='Sod-EntitlementControls-groupControlsArrangeBtns'>
          {(!isInsightsOpen || isSidePanelVisible) && (
            <ButtonIcon
              size='large'
              kind='outlinedNeutral'
              icon={<Icon kind='SaviCompanion' />}
              isDisabled={isInsightsDisabled}
              onClick={handleInsights}
              ariaLabel={l('Open Risk SodInsights Aside')}
              dataTestId={testIds.SOD_ENTITLEMENT_TAB_INSIGHTS_BTN}
            />
          )}
          <Box className='Sod-EntitlementControls-sortColumnListWrapper'>
            <SodSort
              fields={fields}
              fieldName={fieldName ? l(fieldName) : ''}
              actions={actions}
              actionName={actionName ? l(actionName) : ''}
              handleFieldClick={handleFieldClick}
              handleActionClick={handleActionClick}
              isDisabled={isListLoading}
            />
            <Box
              tag='span'
              className='Sod-EntitlementControls-groupControlsArrangeBtnsDivider'
            />
            <EntitlementColumnList
              columns={activeColumns}
              isDisabled={isListLoading}
              handleColumnsChange={handleColumnsChange}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default memo(EntitlementControls);
